import React, { useState, useEffect, useRef } from 'react';
import { Search, Trash2, Edit2, ArrowLeft, ShieldOff, Globe, Monitor, Users, ChevronDown, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import { fetchBlockRules, createBlockRule, updateBlockRule, deleteBlockRule } from '../store/blockRuleSlice';
import { useConfirm } from '../context/ConfirmContext';
import { API_BASE_URL } from '../config';

const CustomSelect = ({ value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  return (
    <div ref={dropdownRef} style={{ position: 'relative', width: '100%' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%',
          background: 'transparent',
          border: 'none',
          color: 'var(--text-primary)',
          fontSize: 14,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          padding: 0,
          outline: 'none'
        }}
      >
        <span>{value}</span>
        <ChevronDown size={14} color="var(--text-secondary)" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 200ms' }} />
      </button>

      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: -16,
          width: 'calc(100% + 32px)',
          marginTop: 16,
          background: 'var(--bg-card)',
          border: '1px solid var(--border-light)',
          borderRadius: 8,
          padding: 4,
          zIndex: 60,
          boxShadow: '0 8px 30px rgba(0,0,0,0.3)'
        }}>
          {options.map(opt => (
            <button
              key={opt}
              onClick={() => { onChange(opt); setOpen(false); }}
              style={{
                width: '100%',
                background: value === opt ? 'rgba(var(--app-primary-color-rgb), 0.12)' : 'transparent',
                color: value === opt ? 'var(--accent-primary)' : 'var(--text-secondary)',
                padding: '10px 12px',
                borderRadius: 4,
                fontSize: 13,
                textAlign: 'left',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(var(--app-primary-color-rgb), 0.08)' }}
              onMouseLeave={e => { if (value !== opt) e.currentTarget.style.background = 'transparent' }}
            >
              <span>{opt}</span>
              {value === opt && <Check size={14} color="var(--accent-primary)" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const getAdminId = () => {
  return Cookies.get('adminId');
};

const WebsiteBlockMaster = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { rules, loading } = useSelector((state) => state.blockRule);

  const [searchQuery, setSearchQuery] = useState('');
  const [blockType, setBlockType] = useState('Application (.exe)');
  const [target, setTarget] = useState('');
  const [applyTo, setApplyTo] = useState('All Employees');
  const [devices, setDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [fetchingDevices, setFetchingDevices] = useState(false);
  const [editId, setEditId] = useState(null);
  const confirm = useConfirm();

  useEffect(() => {
    dispatch(fetchBlockRules(getAdminId()));

    const fetchDevices = async () => {
      try {
        setFetchingDevices(true);
        const response = await fetch(`${API_BASE_URL}/devices?adminId=${getAdminId()}`);
        if (response.ok) {
          const data = await response.json();
          setDevices(data);
        }
      } catch (err) {
        console.error('Failed to fetch devices', err);
      } finally {
        setFetchingDevices(false);
      }
    };
    fetchDevices();
  }, [dispatch]);

  const handleBlock = async () => {
    if (!target.trim()) return;
    const adminId = getAdminId();

    if (editId) {
      await dispatch(updateBlockRule({
        id: editId,
        adminId,
        blockType,
        target,
        applyTo,
        appliedDevices: selectedDevices
      }));
      setEditId(null);
    } else {
      await dispatch(createBlockRule({
        adminId,
        blockType,
        target,
        applyTo,
        appliedDevices: selectedDevices
      }));
    }

    setTarget('');
    setSelectedDevices([]);
  };

  const handleEdit = (rule) => {
    setEditId(rule._id || rule.id);
    setBlockType(rule.type === 'app' ? 'Application (.exe)' : 'Website');
    setTarget(rule.target);
    setApplyTo(rule.applyToAll ? 'All Employees' : 'Specific Employees');
    setSelectedDevices(rule.appliedDevices || []);
  };

  const handleDelete = async (id) => {
    const isConfirmed = await confirm('Are you sure you want to remove this block rule?');
    if (!isConfirmed) return;
    await dispatch(deleteBlockRule({ id, adminId: getAdminId() }));
  };

  const handleCancel = () => {
    setTarget('');
    setBlockType('Application (.exe)');
    setApplyTo('All Employees');
    setSelectedDevices([]);
    setEditId(null);
  };

  const filteredRules = (rules || []).filter(rule => {
    const typeLabel = rule.type === 'app' ? 'Application (.exe)' : rule.type === 'website' ? 'Website' : rule.blockType || '';
    const searchString = `${rule.target || ''} ${typeLabel}`.toLowerCase();
    return searchString.includes(searchQuery.toLowerCase());
  });

  return (
    <div style={{ padding: '24px', flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>

      {/* Header section */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 32, gap: 16 }}>
        <button
          onClick={() => navigate('/masters')}
          style={{
            background: 'var(--input-bg)',
            border: '1px solid var(--border-light)',
            color: 'var(--text-primary)',
            padding: '8px 16px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            fontSize: 14
          }}
        >
          <ArrowLeft size={16} />
          All Masters
        </button>
        <div>
          <h1 style={{ margin: '0 0 4px 0', fontSize: 24, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <ShieldOff size={24} color="#EF4444" />
            App & Website Blocker
          </h1>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary)' }}>Setup domain filtering rules, categories block list and active directory policies.</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 24, flex: 1, flexWrap: 'wrap' }}>
        {/* Left Pane - Form */}
        <div className="dash-glass-card" style={{ flex: '1 1 450px', maxWidth: '100%', padding: 24, display: 'flex', flexDirection: 'column', gap: 24, height: 'fit-content' }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>
            {editId ? 'Update Block Rule' : 'Add New Block Rule'}
          </h2>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {/* Block Type */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Block Type</label>
              <div style={{
                background: 'var(--input-bg)',
                border: '1px solid var(--border-light)',
                borderRadius: 8,
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                height: 42
              }}>
                <CustomSelect
                  value={blockType}
                  options={['Application (.exe)', 'Website']}
                  onChange={setBlockType}
                />
              </div>
            </div>

            {/* Target */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 2, minWidth: 200 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Target</label>
              <div style={{
                background: 'var(--input-bg)',
                border: '1px solid var(--border-light)',
                borderRadius: 8,
                padding: '12px 16px',
                display: 'flex',
                gap: 12,
                alignItems: 'center',
                height: 42
              }}>
                <input
                  type="text"
                  placeholder="Target (e.g. vlc.exe, spotify.com)"
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-primary)',
                    fontSize: 14,
                    width: '100%',
                    outline: 'none'
                  }}
                />
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Apply To</label>
            <div style={{ display: 'flex', gap: 24 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: applyTo === 'All Employees' ? 'var(--text-primary)' : 'var(--text-muted)', cursor: 'pointer', fontSize: 14 }}>
                <div style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${applyTo === 'All Employees' ? 'var(--accent-primary)' : 'var(--border-light)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {applyTo === 'All Employees' && <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent-primary)' }} />}
                </div>
                <input type="radio" checked={applyTo === 'All Employees'} onChange={() => setApplyTo('All Employees')} style={{ display: 'none' }} />
                All Employees
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: applyTo === 'Specific Employees' ? 'var(--text-primary)' : 'var(--text-muted)', cursor: 'pointer', fontSize: 14 }}>
                <div style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${applyTo === 'Specific Employees' ? 'var(--accent-primary)' : 'var(--border-light)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {applyTo === 'Specific Employees' && <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent-primary)' }} />}
                </div>
                <input type="radio" checked={applyTo === 'Specific Employees'} onChange={() => setApplyTo('Specific Employees')} style={{ display: 'none' }} />
                Specific Employees
              </label>
            </div>

            {applyTo === 'Specific Employees' && (
              <div style={{
                background: 'var(--input-bg)',
                border: '1px solid var(--border-light)',
                borderRadius: 8,
                padding: 16,
                marginTop: 8,
                maxHeight: 200,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 8
              }}>
                {fetchingDevices ? (
                  <div style={{ color: 'var(--text-secondary)', fontSize: 13, padding: 8 }}>Loading devices...</div>
                ) : devices.length === 0 ? (
                  <div style={{ color: 'var(--text-secondary)', fontSize: 13, padding: 8 }}>No devices found.</div>
                ) : (
                  devices.map(device => (
                    <label key={device._id || device.computerName} style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', padding: '6px 8px', borderRadius: 6, background: 'rgba(var(--app-primary-color-rgb), 0.04)' }}>
                      <input
                        type="checkbox"
                        checked={selectedDevices.includes(device._id || device.computerName)}
                        onChange={(e) => {
                          const id = device._id || device.computerName;
                          if (e.target.checked) {
                            setSelectedDevices([...selectedDevices, id]);
                          } else {
                            setSelectedDevices(selectedDevices.filter(d => d !== id));
                          }
                        }}
                        style={{ accentColor: 'var(--accent-primary)', width: 16, height: 16, cursor: 'pointer' }}
                      />
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 500 }}>{device.computerName}</span>
                        {device.assignedUser && <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}>{device.assignedUser}</span>}
                      </div>
                    </label>
                  ))
                )}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
            <button
              onClick={handleCancel}
              style={{
                padding: '10px 24px',
                background: 'var(--input-bg)',
                border: '1px solid var(--border-light)',
                borderRadius: 8,
                color: 'var(--text-primary)',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleBlock}
              style={{
                padding: '10px 24px',
                background: '#EF4444',
                border: 'none',
                borderRadius: 8,
                color: '#fff',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              <ShieldOff size={16} />
              {editId ? 'UPDATE' : 'BLOCK'}
            </button>
          </div>
        </div>

        {/* Right Pane - List */}
        <div className="dash-glass-card" style={{ flex: '2 1 400px', minWidth: 0, padding: 24, display: 'flex', flexDirection: 'column', overflow: 'hidden', height: 'fit-content', maxHeight: 'calc(100vh - 160px)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              background: 'var(--input-bg)',
              border: '1px solid var(--border-light)',
              borderRadius: 8,
              padding: '8px 16px',
              width: '100%',
              maxWidth: 300,
              gap: 12
            }}>
              <Search size={16} color="var(--text-muted)" />
              <input
                type="text"
                placeholder="Search rules..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-primary)',
                  outline: 'none',
                  width: '100%'
                }}
              />
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              Active Block Rules: <span style={{ color: '#EF4444', fontWeight: 600 }}>{rules?.length || 0}</span>
            </div>
          </div>

          <div style={{ overflowX: 'auto', overflowY: 'auto', flex: 1, paddingRight: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr>
                  <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', borderBottom: '1px solid var(--border-light)' }}>Target</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', borderBottom: '1px solid var(--border-light)' }}>Type</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', borderBottom: '1px solid var(--border-light)' }}>Applied To</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', borderBottom: '1px solid var(--border-light)', width: 80, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: 24, color: 'var(--text-secondary)' }}>Loading...</td>
                  </tr>
                ) : filteredRules.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: 24, color: 'var(--text-secondary)' }}>No block rules configured</td>
                  </tr>
                ) : (
                  filteredRules.map((rule) => (
                    <tr key={rule._id || rule.id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <td style={{ padding: '16px', color: 'var(--text-primary)', fontSize: 14, fontWeight: 600 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {(rule.type === 'website' || rule.blockType === 'Website') ? <Globe size={14} color="#3B82F6" /> : <Monitor size={14} color="var(--accent-primary)" />}
                          {rule.target}
                        </div>
                      </td>
                      <td style={{ padding: '16px', color: 'var(--text-secondary)', fontSize: 13 }}>
                        {rule.type === 'app' ? 'Application (.exe)' : rule.type === 'website' ? 'Website' : rule.blockType}
                      </td>
                      <td style={{ padding: '16px', color: 'var(--text-secondary)', fontSize: 13 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Users size={12} color="var(--text-muted)" />
                          {rule.applyToAll ? 'All Employees' : `${rule.appliedDevices?.length || 0} Employees`}
                        </div>
                      </td>
                      <td style={{ padding: '16px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                          <button
                            onClick={() => handleEdit(rule)}
                            style={{
                              background: 'var(--input-bg)',
                              border: '1px solid var(--border-light)',
                              padding: '6px',
                              borderRadius: 4,
                              color: 'var(--text-primary)',
                              cursor: 'pointer'
                            }}
                            title="Edit Rule"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            onClick={() => handleDelete(rule._id || rule.id)}
                            style={{
                              background: 'rgba(239, 68, 68, 0.1)',
                              border: '1px solid rgba(239, 68, 68, 0.3)',
                              padding: '6px',
                              borderRadius: 4,
                              color: '#EF4444',
                              cursor: 'pointer'
                            }}
                            title="Delete Rule"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebsiteBlockMaster;
